/**
 * Beteiligten-Kategorien des Kontakt-Pickers (AddParticipantDialog) und der
 * Kontaktauswahl in der Fallanlage. Reines Datenmodell ohne Vue-Abhängigkeit,
 * damit die Zuordnung Kontakttyp → Fall-Rolle testbar bleibt.
 *
 * Kundenanforderung: "Bei der Aktenanlage sehe ich meine eigenen Kontakte
 * nicht." Ursache war u. a., dass Kontakte vom Typ Kunde/Sonstige/ohne Typ in
 * keiner Kategorie auftauchten. Die Auffangkategorie (`catchAll`) sammelt sie.
 */
import type { Contact } from "@/services/contactModel";
import { getContactDisplayName } from "@/services/contactModel";

export type CategoryPriority = "pflicht" | "empfohlen" | "optional";

export interface ParticipantCategory {
  /** Fall-Rolle (tbl_cases_externals.roles) */
  role: string;
  label: string;
  iconType: string;
  color: string;
  priority: CategoryPriority;
  /** Adressbuch-Kontakttypen, die dieser Kategorie zugeordnet werden */
  contactTypes: readonly string[];
  /** Auffangkategorie: alle Kontakte, die in keiner anderen Kategorie landen */
  catchAll?: boolean;
}

/** Schlüssel der Gesamtansicht "Meine Kontakte" (keine echte Rolle). */
export const ALL_CONTACTS_KEY = "_all";

// Die Beschriftungen der Kategorien, die zugleich Unternehmensrollen sind,
// folgen den Anzeigenamen aus `utils/organizationRoles.ts` (Kundenfeedback
// 13.09.2026: Rollenbezeichnungen vereinheitlichen). Die `role`-Datenwerte
// (tbl_cases_externals.roles) bleiben unveraendert.
export const PARTICIPANT_CATEGORIES: readonly ParticipantCategory[] = [
  { role: "Rechtsanwalt",       label: "Rechtsanwaltskanzlei", iconType: "lawyer",         color: "#1565c0", priority: "optional",  contactTypes: ["Rechtsanwalt"] },
  { role: "Autohaus",           label: "Autohaus/Werkstatt",   iconType: "car-dealer",     color: "#e65100", priority: "optional",  contactTypes: ["Werkstatt"] },
  { role: "Sachverständiger",   label: "Sachverständigenbüro", iconType: "specialist",     color: "#6a1b9a", priority: "empfohlen", contactTypes: ["Sachverständiger"] },
  { role: "Versicherung",       label: "Versicherung",         iconType: "insurance",      color: "#2e7d32", priority: "pflicht",   contactTypes: ["Versicherung"] },
  { role: "Abschleppdienst",    label: "Abschleppdienst",      iconType: "towing-service", color: "#37474f", priority: "optional",  contactTypes: ["Abschleppdienst"] },
  { role: "Autovermietung",     label: "Autovermietung",       iconType: "car-rental",     color: "#00838f", priority: "optional",  contactTypes: ["Mietwagenfirma"] },
  // Inhaber-Feedback 15.08.26, II.11: Fuhrpark (Flottenbetreiber = Kunde mit
  // eigenen Fahrzeugen) und Versicherungsmakler.
  { role: "Fuhrpark",           label: "Fuhrpark",             iconType: "fleet",          color: "#00796b", priority: "optional",  contactTypes: ["Kunde"] },
  { role: "Versicherungsmakler", label: "Versicherungsmakler", iconType: "insurance-broker", color: "#33691e", priority: "optional", contactTypes: ["Versicherung"] },
  { role: "Polizei",            label: "Polizei",              iconType: "police",         color: "#0d47a1", priority: "optional",  contactTypes: ["Polizei"] },
  { role: "Staatsanwaltschaft", label: "Staatsanwaltschaft",   iconType: "prosecutor",     color: "#4e342e", priority: "optional",  contactTypes: ["Staatsanwaltschaft"] },
  { role: "Zeuge",              label: "Zeuge",                iconType: "witness",        color: "#558b2f", priority: "optional",  contactTypes: ["Zeuge"] },
  // Auffangkategorie: Kunde, Sonstige und Kontakte ohne Typ erscheinen hier,
  // statt in keiner Kategorie sichtbar zu sein.
  { role: "Sonstige",           label: "Weitere Kontakte",     iconType: "contact",        color: "#757575", priority: "optional",  contactTypes: ["Sonstige", "Kunde"], catchAll: true },
];

/** Pseudo-Kategorie für die Gesamtansicht (alle Adressbuch-Einträge). */
export const ALL_CONTACTS_CATEGORY: ParticipantCategory = {
  role: ALL_CONTACTS_KEY,
  label: "Meine Kontakte",
  iconType: "users",
  color: "#0076e5",
  priority: "optional",
  contactTypes: [],
};

// Typen, welche die Auffangkategorie ausdrücklich führt. Seit "Fuhrpark"
// (contactTypes: ["Kunde"]) beansprucht eine Rolle den Kundentyp; ein
// Privatmandant wäre dann nur noch unter "Fuhrpark" zu finden. Solche Typen
// bleiben deshalb zusätzlich unter "Weitere Kontakte" sichtbar.
const CATCH_ALL_TYPES: ReadonlySet<string> = new Set(
  PARTICIPANT_CATEGORIES.filter((c) => c.catchAll).flatMap((c) => [...c.contactTypes]),
);

const TYPED_CONTACT_TYPES: ReadonlySet<string> = new Set(
  PARTICIPANT_CATEGORIES
    .filter((c) => !c.catchAll)
    .flatMap((c) => [...c.contactTypes])
    .filter((t) => !CATCH_ALL_TYPES.has(t)),
);

export function findCategory(role: string | null | undefined): ParticipantCategory | undefined {
  if (role === ALL_CONTACTS_KEY) { return ALL_CONTACTS_CATEGORY; }
  return PARTICIPANT_CATEGORIES.find((c) => c.role === role);
}

/** Prüft, ob ein Adressbuch-Kontakt in die Kategorie gehört. */
export function contactMatchesCategory(contact: Pick<Contact, "contactType">, cat: ParticipantCategory): boolean {
  if (cat.role === ALL_CONTACTS_KEY) { return true; }
  const type = contact.contactType || "";
  if (cat.catchAll) { return !TYPED_CONTACT_TYPES.has(type); }
  return cat.contactTypes.includes(type);
}

/** Fall-Rolle, die für einen Kontakttyp vorbelegt wird (änderbar durch den Anwender). */
export function roleForContactType(contactType: string | null | undefined): string {
  const type = contactType ?? "";
  // Typen der Auffangkategorie (z. B. "Kunde") behalten deren Rolle: Ein
  // Kunden-Kontakt ist als Beteiligter in aller Regel kein Fuhrpark.
  const hit = CATCH_ALL_TYPES.has(type)
    ? undefined
    : PARTICIPANT_CATEGORIES.find((c) => !c.catchAll && c.contactTypes.includes(type));
  if (hit) { return hit.role; }
  const fallback = PARTICIPANT_CATEGORIES.find((c) => c.catchAll);
  return fallback ? fallback.role : "Sonstige";
}

export type ParticipantSource = "contacts" | "insurances";

/** Auswahl eines Adressbuch-Kontakts als Beteiligter (z. B. in der Fallanlage). */
export interface ContactParticipantSelection {
  contact: Contact;
  /** Fall-Rolle; aus dem Kontakttyp vorbelegt, vom Anwender änderbar. */
  caseRole: string;
}

/**
 * Standard-Quelle beim Öffnen einer Kategorie.
 *
 * Abwägung: Das Versicherungsverzeichnis ist die einzige Kategorie mit einer
 * zweiten Quelle. Hat der Anwender eigene Versicherungskontakte gepflegt,
 * sind das erfahrungsgemäß seine Sachbearbeiter-Kontakte mit direkter
 * E-Mail – sie sollen ohne zusätzlichen Klick sichtbar sein. Ohne eigene
 * Versicherungskontakte bleibt das Verzeichnis der schnellste Weg, weil es
 * sonst nur einen Leerzustand gäbe.
 */
export function defaultSourceForCategory(role: string, contacts: readonly Pick<Contact, "contactType">[]): ParticipantSource {
  if (role !== "Versicherung") { return "contacts"; }
  const cat = findCategory(role);
  if (!cat) { return "contacts"; }
  const hasOwn = contacts.some((c) => contactMatchesCategory(c, cat));
  return hasOwn ? "contacts" : "insurances";
}

/** Freitextsuche über Anzeigename, Firma, E-Mail und Ort. */
export function matchesContactQuery(contact: Contact, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) { return true; }
  const haystack = [
    getContactDisplayName(contact),
    contact.companyName,
    contact.firstName,
    contact.lastName,
    contact.email,
    contact.city,
    contact.zip,
  ];
  return haystack.some((v) => !!v && v.toLowerCase().includes(q));
}

/** Favoriten zuerst, danach alphabetisch. Liefert eine neue Liste. */
export function sortContactsForOverview<T extends Contact>(contacts: readonly T[]): T[] {
  // Kopie statt In-place-Sort (Array#toSorted ist in der Ziel-Lib nicht verfügbar).
  // eslint-disable-next-line unicorn/no-array-sort
  return [...contacts].sort((a, b) => {
    if (!!a.isFavorite !== !!b.isFavorite) { return a.isFavorite ? -1 : 1; }
    return getContactDisplayName(a).localeCompare(getContactDisplayName(b), "de", { sensitivity: "base" });
  });
}
